/**
 * useEcosystemTaxonomy: read-only accessor for the active ecosystem's
 * taxonomy fields with sensible fallbacks for platform-mode / partially
 * populated tenants.
 *
 * Centralises the defensive shape-checks that every taxonomy consumer would
 * otherwise repeat: `ecosystem?.taxonomy?.sectors || []`, plus the empty
 * arrays vs `null` distinction. Returns a stable-shape object so callers
 * destructure without sprinkling optional-chaining through their JSX.
 *
 * Fallbacks:
 *   - Array fields (sectors / regions / programmingTypes / audiences /
 *     mentorAudiences) default to `[]` so consumers can `.map()`
 *     unconditionally: empty arrays render the "All Sectors" / "All
 *     Regions" base option only.
 *   - `mapCenter` defaults to a generic continental-US centroid so the map
 *     loads even when a tenant hasn't authored a center yet.
 *   - `mapDefaultZoom` defaults to `7`, matching the Wave M schema default.
 *   - `defaultStateCode` defaults to an empty string so "{city}, {code}"
 *     renderers can omit the suffix cleanly without an explicit branch.
 *   - `regionsServedLabel` defaults to `null` so callers can render a
 *     region-neutral fallback string.
 *
 * The hook does NOT fetch anything: it reads the already-resolved ecosystem
 * record EcosystemContext exposes. Cheap to call from any tenant-aware
 * component.
 *
 * @returns {{
 *   sectors: string[],
 *   regions: string[],
 *   programmingTypes: string[],
 *   audiences: string[],
 *   mentorAudiences: string[],
 *   mapCenter: { lat: number, lng: number },
 *   mapDefaultZoom: number,
 *   defaultStateCode: string,
 *   regionsServedLabel: (string|null),
 * }}
 */
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

// Generic continental-US centroid. Used only when the active tenant hasn't
// authored a `taxonomy.mapCenter` yet (or in platform mode where there is
// no tenant). Picked to keep a roughly-centered initial view rather than
// silently snapping to (0,0) off the coast of Africa.
const FALLBACK_MAP_CENTER = Object.freeze({ lat: 39.8283, lng: -98.5795 });

// Schema default from Ecosystem.taxonomy.mapDefaultZoom: duplicated here so
// the hook works even when the tenant record was created before the field
// existed (older records report `undefined` for taxonomy.mapDefaultZoom).
const FALLBACK_MAP_ZOOM = 7;

export default function useEcosystemTaxonomy() {
    const { ecosystem } = useEcosystem();
    const taxonomy = (ecosystem && ecosystem.taxonomy) || {};

    const mapCenter =
        taxonomy.mapCenter &&
        typeof taxonomy.mapCenter.lat === 'number' &&
        typeof taxonomy.mapCenter.lng === 'number'
            ? { lat: taxonomy.mapCenter.lat, lng: taxonomy.mapCenter.lng }
            : FALLBACK_MAP_CENTER;

    return {
        sectors: Array.isArray(taxonomy.sectors) ? taxonomy.sectors : [],
        regions: Array.isArray(taxonomy.regions) ? taxonomy.regions : [],
        programmingTypes: Array.isArray(taxonomy.programmingTypes)
            ? taxonomy.programmingTypes
            : [],
        audiences: Array.isArray(taxonomy.audiences) ? taxonomy.audiences : [],
        mentorAudiences: Array.isArray(taxonomy.mentorAudiences)
            ? taxonomy.mentorAudiences
            : [],
        mapCenter,
        mapDefaultZoom:
            typeof taxonomy.mapDefaultZoom === 'number'
                ? taxonomy.mapDefaultZoom
                : FALLBACK_MAP_ZOOM,
        defaultStateCode:
            typeof taxonomy.defaultStateCode === 'string'
                ? taxonomy.defaultStateCode
                : '',
        regionsServedLabel:
            typeof taxonomy.regionsServedLabel === 'string' &&
            taxonomy.regionsServedLabel.length > 0
                ? taxonomy.regionsServedLabel
                : null,
    };
}
